import _get from 'lodash/get'
import * as z from 'zod'

import { CONTENTS_CONSTANTS } from '@flows/configs/contents/constants'
import { DEDUCTIBLE_HEADER } from '@flows/configs/shared/constants'
import * as schemaUtils from '@flows/configs/shared/schemaUtils'
import type { StepConfigParams } from '@flows/configs/types'
import type { AnyCustomComponent } from '@flows/core/types/CustomComponent'
import type { TransformedOption } from '@flows/core/types/stepContentTypes'
import type { AnyStepConfiguration } from '@flows/core/types/stepTypes'
import { BaseSchema_ContentsV1GetsafeDe } from '@flows/insurance/schemas'
import Schema_Quote from '@flows/insurance/schemas/shared/Schema_Quote'

const contents_quote = (params: StepConfigParams) => {
  const quoteStep = {
    type: 'customComponent',
    name: 'QuoteStep',
    props: {
      product: 'contents_v1_getsafe_de',
      headerSubtitle: {
        type: 'translatableText',
        translationKey: 'sharedFlows_contentsNxt:steps.quote.subtitle',
        values: {
          living_space_square_meters: {
            type: 'dynamicValue',
            path: params.helperFunctions.getParamPath('living_space_square_meters'),
          },
          zip_code: {
            type: 'dynamicValue',
            path: 'zip_code',
          },
          insured_sum_amount: {
            type: 'dynamicValue',
            path: params.helperFunctions.getParamPath('insured_sum_amount'),
          },
        },
      },
      configurableParameters: [
        DEDUCTIBLE_HEADER(),
        {
          type: 'dropDown',
          label: {
            type: 'translatableText',
            translationKey: 'sharedFlows_common:steps.deductibles',
          },
          useFilterInput: false,
          styleType: 'outlined',
          name: params.helperFunctions.getParamPath('deductible_amount'),
          options: CONTENTS_CONSTANTS.deductibles.map((option): TransformedOption => {
            return {
              label: {
                type: 'nonTranslatableText',
                text: params.clientHelpers.formatter.formatCurrency(option),
              },
              value: Number(option),
            }
          }),
        },
      ],
    },
    inputs: [],
    outputs: [],
  } as const satisfies AnyCustomComponent
  return {
    validationStrategy: 'onChange',
    onPrepare: {
      type: 'dispatchedHook',
      callbackConfig: {
        name: 'prepareQuoteStep',
        options: {
          productKey: 'contents_v1_getsafe_de',
        },
        inputs: [],
        outputs: [],
      },
    },
    nonStickyFooter: true,
    stepContent: [quoteStep],
    schema: Schema_Quote.merge(
      schemaUtils.generateZodObjectFromPaths({
        [params.helperFunctions.getParamPath('bike_theft_insured_sum_amount')]:
          BaseSchema_ContentsV1GetsafeDe.shape.bike_theft_insured_sum_amount,
        [params.helperFunctions.getParamPath('bike_theft_coverage')]:
          BaseSchema_ContentsV1GetsafeDe.shape.bike_theft_coverage,
        [params.helperFunctions.getParamPath('glass_coverage')]:
          BaseSchema_ContentsV1GetsafeDe.shape.glass_coverage,
        [params.helperFunctions.getParamPath('natural_disasters_coverage')]:
          BaseSchema_ContentsV1GetsafeDe.shape.natural_disasters_coverage,
        [params.helperFunctions.getParamPath('tariff_line')]:
          BaseSchema_ContentsV1GetsafeDe.shape.tariff_line,
        [params.helperFunctions.getParamPath('deductible_amount')]:
          BaseSchema_ContentsV1GetsafeDe.shape.deductible_amount,
        [params.helperFunctions.getParamPath('insured_sum_amount')]:
          BaseSchema_ContentsV1GetsafeDe.shape.insured_sum_amount,
      })
    ).superRefine((values, ctx) => {
      if (
        [true].includes(
          _get(values, params.helperFunctions.getParamPath('bike_theft_coverage')) ?? false
        )
      ) {
        if (!_get(values, params.helperFunctions.getParamPath('bike_theft_insured_sum_amount'))) {
          ctx.addIssue({
            path: [params.helperFunctions.getParamPath('bike_theft_insured_sum_amount')],
            code: z.ZodIssueCode.invalid_type,
            expected: 'integer',
            received: 'undefined',
            message: 'Required',
          })
        }
      }
    }),
    nextStep: 'emailAndName',
    subFlow: params.subFlow,
  } as const satisfies AnyStepConfiguration
}

export default contents_quote
